import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { useSpeckleStore } from "@/stores/speckle/speckle.store";

export type ToolKey = "section" | "explode" | "measure" | "lights" | "modes" | "inspect" | "heatmap" | "dataQuality";

interface ToolState {
  active: boolean;
  modified: boolean;
  // A tool sets this while it's mid-interaction in a way Escape shouldn't close (e.g. Measure
  // waiting for a vertex-replacement click) — Modal3D's global Escape handler checks it before closing.
  blockEscapeClose: boolean;
}

export type ToolStates = Record<ToolKey, ToolState>;

export const useSpeckleExtensions = defineStore("speckleExtensions", () => {
  const core = useSpeckleStore();

  const toolStates = ref<ToolStates>({
    section: { active: false, modified: false, blockEscapeClose: false },
    explode: { active: false, modified: false, blockEscapeClose: false },
    measure: { active: false, modified: false, blockEscapeClose: false },
    lights: { active: false, modified: false, blockEscapeClose: false },
    modes: { active: false, modified: false, blockEscapeClose: false },
    inspect: { active: false, modified: false, blockEscapeClose: false },
    heatmap: { active: false, modified: false, blockEscapeClose: false },
    dataQuality: { active: false, modified: false, blockEscapeClose: false },
  });

  const anyToolsActive = computed(() => Object.values(toolStates.value).some((tool) => tool.active));

  // Deactivate every viewer tool and drop its "modified" flag. Each tool's component watches its own
  // `active` flag and tears down its action (selection, isolation, section plane, measurement, …) when
  // it flips false, so this single reset cleans up the whole toolbar.
  const resetToolStates = () => {
    for (const tool of Object.values(toolStates.value)) {
      tool.active = false;
      tool.modified = false;
      tool.blockEscapeClose = false;
    }
  };

  // Switching projects tears the viewer down (disposeViewer → viewer = null) and builds a new one when
  // the next model view mounts. This store is an app-lifetime singleton, so a tool left active in the
  // previous project (Inspect/Select, a section plane, …) would otherwise carry over. Reset on any
  // viewer-instance change so the new project's model always starts with a clean toolbar.
  watch(
    () => core.viewer,
    () => resetToolStates()
  );

  return {
    // state
    toolStates,

    // actions
    resetToolStates,

    // computed
    anyToolsActive,
  };
});
